import { Component, OnInit, Input } from '@angular/core';

import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

import * as $ from 'jquery';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Component({
  selector: 'ag-grid-excel',
  templateUrl: './ag-grid-excel.component.html',
  styleUrls: ['./ag-grid-excel.component.scss']
})
export class AgGridExcelComponent implements OnInit {

  @Input() gridApi: any;
  @Input() selected: string;
  @Input() filename: string;
  @Input() title: string;

  private excelFileName: string;
  private dataList = [];
  private dataStr: string = '';

  // 숫자 필드 패턴 정의 (필요에 따라 추가/수정)
  private numberFields = [
    'seq', 'seqNo', 'seqNo', 'seq_no',
    'fee', 'feeAmount', 'fee_amount', 'amount', 'price', 'cost',
    'count', 'cnt', 'count', 'total', 'sum',
    'date', 'year', 'month', 'day',
    'code', 'id', 'no', 'number',
    'capacity', 'size', 'weight', 'length', 'width', 'height',
    'rate', 'ratio', 'percent', 'percentage',
    'inspection_fee', 'inspection_tax', 'inspection_fee_total',
    'frozen_capacity', 'plan_date', 'inspection_date', 'due_date',
    'certificate_date', 'reinspection_date', 'next_inspection_date',
    // 추가 숫자 필드 패턴
    'reception_seq', 'facility_code', 'company_code', 'region_code', 'city_code',
    'member_grade', 'inspection_type', 'refrigerant_gas_code', 'inspection_agency',
    'mem_no', 'usage_code', 'dm_yn', 'trm_yn', 'use_status',
    'zip_no', 'business_number', 'person_name', 'safety_officer',
    'reception_number', 'reception_status', 'inspection_yn', 'pass_yn',
    'approval_yn', 'certificate_number', 'certificate_status',
    // 수수료 관련 필드 (camelCase 포함)
    'inspectionFeeTotal', 'inspectionFee', 'inspectionTax', 'feeTotal', 'feeAmount',
    'taxAmount', 'totalAmount', 'totalFee', 'totalTax', 'amount', 'price', 'cost',
    'charge', 'rate', 'percentage', 'ratio', 'percent'
  ];

  constructor(
    private toastrService: ToastrService
  ) {
  }

  ngOnInit() {
    this.excelFileName = this.filename + "_" + moment().format('YYYYMMDD') + '.xlsx';
  }

  /**
   * 숫자 필드인지 확인하는 함수
   */
  private isNumberField(fieldName: string): boolean {
    const lowerFieldName = fieldName.toLowerCase();

    // 한국어 헤더명 명시적 확인
    if (fieldName === '수수료' || fieldName === '검사수수료' || fieldName === '세금' ||
        fieldName === '합계' || fieldName === '총액' || fieldName === '금액' ||
        fieldName === '요금' || fieldName === '비용' || fieldName === '가격') {
      return true;
    }

    // 명시적으로 정의된 패턴 확인
    if (this.numberFields.some(pattern => lowerFieldName.includes(pattern.toLowerCase()))) {
      return true;
    }

    // 추가 패턴 매칭
    if (lowerFieldName.includes('fee') || lowerFieldName.includes('tax') ||
        lowerFieldName.includes('amount') || lowerFieldName.includes('total') ||
        lowerFieldName.includes('price') || lowerFieldName.includes('cost') ||
        lowerFieldName.includes('count') || lowerFieldName.includes('seq') ||
        lowerFieldName.includes('no') || lowerFieldName.includes('code') ||
        lowerFieldName.includes('id') || lowerFieldName.includes('number')) {
      return true;
    }

    // 숫자로 끝나는 필드 (예: inspectionFee1, amount2 등)
    if (/\d$/.test(fieldName)) {
      return true;
    }

    return false;
  }

  /**
   * 값이 숫자로 변환 가능한지 확인하는 함수
   */
  private isNumeric(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    // 문자열인 경우 콤마 제거 후 확인
    if (typeof value === 'string') {
      const cleanValue = value.replace(/[,₩$]/g, '').trim();
      return !isNaN(Number(cleanValue)) && isFinite(Number(cleanValue));
    }

    return !isNaN(Number(value)) && isFinite(Number(value));
  }

    /**
   * 데이터 타입을 적절하게 변환하는 함수
   */
  private convertDataType(fieldName: string, value: any): any {
    // 숫자 필드인 경우 강제로 숫자 변환 시도
    if (this.isNumberField(fieldName)) {
      if (value === null || value === undefined || value === '') {
        return 0; // 빈 값은 0으로 처리
      }

      // 문자열인 경우 정리 후 숫자로 변환
      if (typeof value === 'string') {
        const cleanValue = value.replace(/[,₩$\s]/g, '').trim();
        const numValue = parseFloat(cleanValue);
        return isNaN(numValue) ? 0 : numValue;
      }

      // 이미 숫자인 경우
      const numValue = Number(value);
      return isNaN(numValue) ? 0 : numValue;
    }

    // 날짜 필드인 경우 (moment.js 사용)
    if (fieldName.toLowerCase().includes('date') && value) {
      const momentDate = moment(value);
      if (momentDate.isValid()) {
        return momentDate.format('YYYY-MM-DD');
      }
    }

    // 기본값 반환
    return value;
  }

  /**
   * 워크시트에서 숫자 필드의 셀 타입을 강제로 숫자로 지정
   */
  private applyNumberCellTypes(worksheet: XLSX.WorkSheet, headers: any[]): void {
    const range = XLSX.utils.decode_range(worksheet['!ref']);

    // 헤더 인덱스 매핑
    const headerIndexMap = new Map<string, number>();
    headers.forEach((header, index) => {
      const headerName = header.colDef.headerName;
      headerIndexMap.set(headerName, index);
    });

    // 데이터 행을 순회하면서 숫자 필드 셀 타입 지정
    for (let row = 3; row <= range.e.r; row++) { // 3행부터 시작 (0: 제목, 1: 빈줄, 2: 헤더)
      headers.forEach((header, colIndex) => {
        const headerName = header.colDef.headerName;

        if (this.isNumberField(headerName)) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIndex });
          const cell = worksheet[cellAddress];

          if (cell && cell.v !== null && cell.v !== undefined && cell.v !== '') {
            // 셀 타입을 숫자로 강제 지정
            cell.t = 'n'; // number type
            cell.v = Number(cell.v); // 값도 숫자로 변환

            console.log(`Applied number format to cell ${cellAddress}: ${cell.v} (type: ${cell.t})`);
          }
        }
      });
    }
  }

  excelExport() {
    let selectCheck = ( this.selected == 'true' ) ? true : false;
    let obj = this.gridApi.rowModel.nodeManager.allNodesMap;
    let header = this.gridApi.columnController.gridColumns;
    let columnWidth = [];

    this.dataList = [];

    for( const index in obj ) {
      let addCheck = false;

      if( selectCheck ) {
        if( obj[index].selected ) addCheck = true;
      } else {
        addCheck = true;
      }

      if( addCheck ) {
        let tmp = {};
        for( let i in header ) {
          const fieldName = header[i].colDef.headerName;
          const fieldValue = obj[index].data[ header[i].colId ];

          // 데이터 타입 변환 적용
          const convertedValue = this.convertDataType(fieldName, fieldValue);
          tmp[fieldName] = convertedValue;

          // 디버깅: 숫자 필드 변환 로그
          if (this.isNumberField(fieldName)) {
            console.log(`🔢 Field: "${fieldName}", Original: "${fieldValue}" (${typeof fieldValue}), Converted: ${convertedValue} (${typeof convertedValue})`);
          }

          columnWidth.push( { wpx: header[i].actualWidth } );
        }

        this.dataList.push( tmp );
      }
    }

    if( this.dataList.length < 1 ) {
      this.toastrService.error( '엑셀로 출력할 데이터가 없습니다.', '' );
      return false;
    } else {

      let title = [ [ this.title ], [] ];
      var worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([]);

      // 제목 추가
      worksheet = XLSX.utils.sheet_add_aoa( worksheet, title );
      // 색상변경 등은 프로 유료 버전에서만 지원
      //worksheet['A1'] = {v: this.title, t: "s", s: { alignment: 'center', font: {size: 14, bold: true, color: '#FF00FF' }}};

      /*
        s: { alignment: {textRotation: 90 },
        font: {sz: 14, bold: true, color: #FF00FF }
      */

      // 데이터 추가 (타입 정보 포함)
      worksheet = XLSX.utils.sheet_add_json( worksheet, this.dataList, {
        origin: "A3",
        skipHeader: false,
        dateNF: 'yyyy-mm-dd'
      } );

      // 숫자 셀 타입 강제 지정
      this.applyNumberCellTypes(worksheet, header);

      // 워크북 생성
      var workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };

      // 컬럼 사이즈 지정
      worksheet['!cols'] = columnWidth;
      /*
      열 머지 처리
      worksheet['!merges'] = [
        {s:{r:0,c:0},e:{r:0,c: header.length } },
        {s:{r:1,c:0},e:{r:1,c: header.length } }
      ];
      */

      // 엑셀 버퍼로 보내기
      var excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      // 파일로 저장
      this.saveAsExcelFile(excelBuffer, this.excelFileName);

      return true;
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
     const data: Blob = new Blob([buffer], {type: EXCEL_TYPE});

     FileSaver.saveAs( data, fileName );
  }

}
